import api from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// Lokale Zeit "HH:MM" → UTC "HH:MM"
function localTimeToUtc(localTimeStr) {
  const [h, m] = localTimeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  const utcH = String(d.getUTCHours()).padStart(2, '0');
  const utcM = String(d.getUTCMinutes()).padStart(2, '0');
  return `${utcH}:${utcM}`;
}

function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';
  } catch {
    return 'Europe/Berlin';
  }
}

export async function registerPush(preferredLocalTime = '08:00') {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Web Push wird von diesem Browser nicht unterstützt.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Benachrichtigungen wurden abgelehnt.');
  }

  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  const result = await api.request('/push/vapid-key', { skipAuthRedirect: true });
  const publicKey = result?.publicKey;
  if (!publicKey) throw new Error('VAPID-Key nicht verfügbar.');

  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const sub = subscription.toJSON();
  await api.request('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      endpoint:      sub.endpoint,
      keys:          { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
      preferredTime: localTimeToUtc(preferredLocalTime),
      timezone:      getUserTimezone(),
    }),
  });

  return true;
}

export async function updatePushTime(preferredLocalTime) {
  await api.request('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      // UPSERT — endpoint/keys aus bestehender Subscription holen
      ...(await _getCurrentSubJson()),
      preferredTime: localTimeToUtc(preferredLocalTime),
      timezone:      getUserTimezone(),
    }),
  });
}

async function _getCurrentSubJson() {
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) throw new Error('Kein Service Worker registriert.');
  const sub = await reg.pushManager.getSubscription();
  if (!sub) throw new Error('Keine aktive Push-Subscription.');
  const j = sub.toJSON();
  return { endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } };
}

export async function unregisterPush() {
  await api.request('/push/unsubscribe', { method: 'DELETE' });

  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }
  }
}

export async function isPushEnabled() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  if (Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
